import { Checkbox, Grid, Select, Switch, Text } from '@mantine/core';

import { CARBON_ANALYTIC_OPTIONS, THIRTY_CM_STR } from 'constants/projects';

import InputAndLabelGridRow from 'common/Components/InputAndLabelGridRow';

import { useProjectsTranslations } from '../hooks';
import { useSamplePlanFormContext } from '../samplePlanFormContextV2';
import { getDensityOptionsFromConfig } from '../utils';

import { useCarbonPlanConfigQuery } from './queries';

const ThirtyCmSampleDetails = () => {
  const translations = useProjectsTranslations();

  const samplePlanForm = useSamplePlanFormContext();
  const formValues = samplePlanForm.getValues();

  const { samplePlanConfig } = useCarbonPlanConfigQuery();
  const { thirty_cm_core } = samplePlanConfig || {};

  return (
    <Grid mt="md">
      <InputAndLabelGridRow
        elemId="30cm-details-switch"
        label={translations.carbon30cmSampleDetails}
        fw="bold"
        size="xl"
        span={5}
        input={
          <Switch
            id="carbon30cm-details-switch"
            {...samplePlanForm.getInputProps('isThirtyCmCore', {
              type: 'checkbox',
            })}
            checked={formValues.isThirtyCmCore}
            onChange={() =>
              samplePlanForm.setFieldValue('isThirtyCmCore', !formValues.isThirtyCmCore)
            }
            disabled={!samplePlanConfig?.depths.includes(THIRTY_CM_STR)}
          />
        }
      />
      {formValues.isThirtyCmCore && (
        <>
          <InputAndLabelGridRow
            elemId="30cm-lab"
            label={translations.lab}
            span={5}
            input={
              <Select
                data={thirty_cm_core?.labs}
                id="carbon30cm-lab"
                {...samplePlanForm.getInputProps('step_probe_dict.lab')}
              />
            }
          />
          <InputAndLabelGridRow
            elemId="30cm-core-analytic-select"
            label={translations.analytics}
            span={5}
            input={
              <Grid align="center">
                <Grid.Col span={5}>
                  <Select
                    data={CARBON_ANALYTIC_OPTIONS}
                    id="30cm-core-analytic-select"
                    {...samplePlanForm.getInputProps('step_probe_dict.sic')}
                    value={formValues.step_probe_dict.sic}
                    onChange={(value) =>
                      value && samplePlanForm.setFieldValue('step_probe_dict.sic', value)
                    }
                  />
                </Grid.Col>
                <Grid.Col span={1}>
                  <Checkbox
                    id="30cm-add-texture-ph"
                    {...samplePlanForm.getInputProps('step_probe_dict.add_texture_ph', {
                      type: 'checkbox',
                    })}
                    checked={formValues.step_probe_dict.add_texture_ph}
                    disabled={thirty_cm_core?.add_texture_and_ph.required}
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <Text size="xs">{translations.addTexturePhMsg}</Text>
                </Grid.Col>
              </Grid>
            }
          />
          <InputAndLabelGridRow
            elemId="30cm-density-select"
            label={translations.density}
            span={5}
            input={
              <Select
                data={getDensityOptionsFromConfig(thirty_cm_core?.densities || [])}
                id="30cm-density-select"
                allowDeselect={false}
                {...samplePlanForm.getInputProps('step_probe_dict.density')}
                value={String(formValues.step_probe_dict.density)}
                onChange={(value) =>
                  value && samplePlanForm.setFieldValue('step_probe_dict.density', Number(value))
                }
              />
            }
          />
          <InputAndLabelGridRow
            elemId="30cm-reduce-bd"
            label={`${translations.reduceBDSamples}?`}
            span={5}
            input={
              <Grid align="center">
                <Grid.Col span={1}>
                  <Checkbox
                    id="30cm-reduce-bd"
                    {...samplePlanForm.getInputProps('step_probe_dict.reduced_bd', {
                      type: 'checkbox',
                    })}
                    checked={formValues.step_probe_dict.reduced_bd}
                    disabled={thirty_cm_core?.reduced_bd.required}
                  />
                </Grid.Col>
                <Grid.Col span={11}>
                  <Text size="xs">{translations.onePerFieldSocBdMsg}</Text>
                </Grid.Col>
              </Grid>
            }
          />
          {formValues.step_probe_dict.add_texture_ph && (
            <InputAndLabelGridRow
              elemId="30cm-core-texture-density-select"
              label={translations.textureDensity}
              span={5}
              input={
                <Select
                  data={thirty_cm_core?.texture_densities || []}
                  id="30cm-core-texture-density-select"
                  allowDeselect={false}
                  {...samplePlanForm.getInputProps('step_probe_dict.texture_density')}
                />
              }
            />
          )}
          <InputAndLabelGridRow
            elemId="30cm-split-core"
            label={`${translations.splitCore}?`}
            span={5}
            input={
              <Grid align="center">
                <Grid.Col span={1}>
                  <Checkbox
                    id="30cm-split-core"
                    {...samplePlanForm.getInputProps('step_probe_dict.is_split_core', {
                      type: 'checkbox',
                    })}
                    checked={formValues.step_probe_dict.is_split_core}
                    disabled={thirty_cm_core?.is_split_core.required}
                  />
                </Grid.Col>
                <Grid.Col span={11}>
                  <Text size="xs">0-15, 15-30 cm</Text>
                </Grid.Col>
              </Grid>
            }
          />
          <InputAndLabelGridRow
            elemId="30cm-min-1-per-field"
            label={translations.min1PerField}
            span={5}
            input={
              <Grid align="center">
                <Grid.Col span={1}>
                  <Checkbox
                    id="30cm-min-1-per-field"
                    {...samplePlanForm.getInputProps('step_probe_dict.field_requirement', {
                      type: 'checkbox',
                    })}
                    checked={formValues.step_probe_dict.field_requirement}
                    disabled={thirty_cm_core?.min_one_per_field.required}
                  />
                </Grid.Col>
                <Grid.Col span={11}>
                  <Text size="xs">{translations.minOnePerFieldMsg}</Text>
                </Grid.Col>
              </Grid>
            }
          />
        </>
      )}
    </Grid>
  );
};

export default ThirtyCmSampleDetails;
